import { useCurrency, useSelectedCurrency } from '../hooks';
import { useState, useEffect, useMemo } from 'react';

export interface ConvertedPrice {
  originalPrice: number;
  originalCurrency: string;
  convertedPrice: number;
  convertedCurrency: string;
  rate: number;
  isConverted: boolean;
}

export const usePriceConversion = (originalPrice: number, originalCurrency: string) => {
  const { selectedCurrency } = useSelectedCurrency();
  const { getRate } = useCurrency();
  const [convertedPrice, setConvertedPrice] = useState<ConvertedPrice | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if conversion is needed
  const needsConversion = useMemo(() => {
    return originalCurrency !== selectedCurrency && originalPrice > 0;
  }, [originalCurrency, selectedCurrency, originalPrice]);

  useEffect(() => {
    if (!needsConversion) {
      const result = {
        originalPrice,
        originalCurrency,
        convertedPrice: originalPrice,
        convertedCurrency: originalCurrency,
        rate: 1,
        isConverted: false,
      };
      setConvertedPrice(result);
      return;
    }

    const convertPrice = async () => {
      setLoading(true);
      setError(null);

      try {
        const rate = await getRate(originalCurrency, selectedCurrency);
        const converted = originalPrice * rate.rate;

        const result = {
          originalPrice,
          originalCurrency,
          convertedPrice: converted,
          convertedCurrency: selectedCurrency,
          rate: rate.rate,
          isConverted: true,
        };
        setConvertedPrice(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Currency conversion failed');
        // Fallback to original price if conversion fails
        const fallbackResult = {
          originalPrice,
          originalCurrency,
          convertedPrice: originalPrice,
          convertedCurrency: originalCurrency,
          rate: 1,
          isConverted: false,
        };
        setConvertedPrice(fallbackResult);
      } finally {
        setLoading(false);
      }
    };

    convertPrice();
  }, [originalPrice, originalCurrency, selectedCurrency, needsConversion, getRate]);

  return {
    convertedPrice,
    loading,
    error,
    needsConversion,
  };
};
